/**
 * L'appui franc : sur un téléphone, un doigt posé sur une case pour faire
 * défiler la liste ne doit pas la cocher.
 *
 * Constat du 02/11/2026 : les douze présences pointées depuis Gestion ont toutes
 * été décochées dans les dix secondes, parce que le navigateur transforme en
 * clic un contact qui bouge à peine (jusqu'à une quinzaine de pixels) ou qui
 * arrête un défilement lancé — et Chrome place ce clic au point de départ du
 * doigt : le déplacement se suit donc pendant tout le contact, pas entre le
 * contact et le clic. La règle : un geste au doigt ne compte que s'il est bref,
 * presque immobile, et qu'aucun défilement n'a eu lieu pendant le contact ni
 * juste avant. La souris et le clavier comptent toujours.
 *
 * Un second geste sur la même case dans la foulée (double appui involontaire)
 * est ignoré : la case garde l'état du premier. Une correction volontaire, une
 * seconde plus tard, passe. PROPOSITION TECHNIQUE. [cdv:garderie]
 */

export interface Appui {
  /** Doigt ou stylet ; une souris clique toujours franchement. */
  tactile: boolean;
  /** Le plus grand écart du doigt depuis le contact, jusqu'au relâchement, en pixels CSS. */
  deplacement: number;
  /** La durée du contact, en millisecondes. */
  duree: number;
  /** Un défilement a eu lieu pendant le contact, ou juste avant (un doigt qui arrête la liste). */
  defilement: boolean;
  /** Le navigateur a repris le geste pour lui (`pointercancel`) : c'était un défilement. */
  annule?: boolean;
}

export const SEUILS_DE_L_APPUI = {
  /** Au-delà, le doigt glissait : il faisait défiler. */
  deplacementPx: 10,
  /** Au-delà, le doigt s'attardait : il tenait la liste, il ne tapait pas. */
  dureeMs: 500,
  /** Un défilement survenu si peu de temps avant le contact : le doigt arrêtait la liste qui filait. */
  avantLeContactMs: 150,
  /** Un second geste sur la même case dans ce délai est un rebond, pas une correction. */
  rebondMs: 700,
} as const;

export function estUnAppuiFranc(a: Appui): boolean {
  if (!a.tactile) return true;
  return !a.annule && a.deplacement <= SEUILS_DE_L_APPUI.deplacementPx && a.duree <= SEUILS_DE_L_APPUI.dureeMs && !a.defilement;
}

/**
 * Le défilement compte s'il est survenu après le contact, ou dans les
 * `avantLeContactMs` qui le précèdent. Les instants sont ceux de `performance.now()`.
 */
export function aDefile(dernierDefilement: number, debutDuContact: number): boolean {
  return dernierDefilement >= debutDuContact - SEUILS_DE_L_APPUI.avantLeContactMs;
}

/** Vrai quand un geste sur cette case suit le précédent de trop près pour être voulu. */
export function estUnRebond(precedent: number | undefined, maintenant: number): boolean {
  return precedent !== undefined && maintenant - precedent < SEUILS_DE_L_APPUI.rebondMs;
}
